/**
 * SnapshotView — page de consultation privée d'un snapshot, autonome
 * (pas de sidebar/AppLayout). Charge le détail par id et rend le header +
 * les pages figées via SnapshotRenderer.
 */
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { getSnapshot, getSnapshotForRender, downloadSnapshotPdf } from '../features/snapshots/snapshotsApi'
import { SnapshotHeader } from '../features/snapshots/SnapshotHeader'
import { SnapshotRenderer } from '../features/snapshots/SnapshotRenderer'
import type { SnapshotDetail } from '../types/snapshot'

export default function SnapshotView() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const pdfToken = params.get('pdf_token')
  const [detail, setDetail] = useState<SnapshotDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    setError(null)
    const fetchDetail = pdfToken ? getSnapshotForRender(Number(id), pdfToken) : getSnapshot(Number(id))
    fetchDetail
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch(() => {
        if (!cancelled) setError('Snapshot introuvable')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, pdfToken])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: '#F4F5FA' }}>
        <p className="text-sm" style={{ color: '#8B8FA7' }}>
          Chargement…
        </p>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: '#F4F5FA' }}>
        <p className="text-sm font-medium" style={{ color: '#1B2559' }}>
          Snapshot introuvable
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F5FA' }}>
      <div className="snapshot-noprint">
        <SnapshotHeader
          reportName={detail.report.name}
          clientName={detail.client.name}
          periodStart={detail.snapshot.periodStart}
          periodEnd={detail.snapshot.periodEnd}
        />
        <div className="flex justify-end gap-2 px-6 pt-4">
          <button
            type="button"
            onClick={() => downloadSnapshotPdf(Number(id))}
            className="rounded-lg border border-[#D9DBE9] bg-white px-4 py-2 text-sm font-medium text-[#1B2559] hover:bg-[#F4F5FA]"
          >
            Télécharger PDF
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-[#4F46E5]"
          >
            Imprimer / PDF
          </button>
        </div>
      </div>
      <div className="snapshot-canvas flex justify-center px-6 py-8">
        <SnapshotRenderer pagesFrozen={detail.snapshot.pagesFrozen} dataFrozen={detail.snapshot.dataFrozen} />
      </div>
    </div>
  )
}
